"use client";

import { Switch as SwitchPrimitive } from "@base-ui/react/switch";

import { cn } from "../../lib/utils";

/**
 * `onCheckedChange` narrowed back to Radix's signature.
 *
 * Base UI passes a second `eventDetails` argument. Nothing in the app wants it,
 * and leaking it would widen the callback every one of the 126 importing files
 * is typed against — so it is dropped here rather than forwarded.
 */
type SwitchProps = Omit<
  SwitchPrimitive.Root.Props,
  "onCheckedChange" | "className"
> & {
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
};

function Switch({ className, onCheckedChange, ...props }: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      // Base UI's root renders a `<span>`, which computes `display: inline` and
      // therefore ignores the track's `h-[1.15rem] w-8` outright — the same
      // collapse that hit Checkbox's `size-4`. Rendering the `<button>` Radix
      // rendered restores the box, and `nativeButton` additionally keeps the
      // caller's `id` on the root (Base UI otherwise moves it to the hidden
      // input), so existing `<label htmlFor>` pairings keep working unchanged.
      // oxlint-disable-next-line jsx-a11y/control-has-associated-label -- render template only; Base UI merges the caller's aria-label/aria-labelledby/id (for <label htmlFor>) onto it
      render={<button type="button" />}
      nativeButton
      data-slot="switch"
      onCheckedChange={(checked) => onCheckedChange?.(checked)}
      className={cn(
        "peer data-[checked]:bg-primary data-[unchecked]:bg-input focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[unchecked]:bg-input/80 inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "bg-background dark:data-[unchecked]:bg-foreground dark:data-[checked]:bg-primary-foreground pointer-events-none block size-4 rounded-full ring-0 transition-transform data-[checked]:translate-x-[calc(100%-2px)] data-[unchecked]:translate-x-0",
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
